/**
 * Wie ein Horizontsymbol gelesen und eingefaerbt wird.
 *
 * Reine Funktionen ohne Angular, damit sie ohne TestBed pruefbar sind.
 *
 * ⚠ Die Farben sind zum groesseren Teil eine DARSTELLUNGSENTSCHEIDUNG, keine
 * Messung: Sie sollen die Horizonte auf einen Blick unterscheidbar machen und
 * behaupten nicht, wie der Boden wirklich aussieht. Wo die SEA die Farbe selbst
 * nennt, ist sie uebernommen und der Wortlaut steht dabei. Eine verbindliche
 * Tabelle „Horizont zu Farbwert" gibt es nicht; Munsell beschreibt die wirklich
 * gemessene Farbe, und die ist von Standort zu Standort verschieden.
 */

export interface HorizontEintrag {
	farbe: string;
	/** Klartext des Horizontes - das, was in Legende und Tafel steht. */
	text: string;
	/**
	 * Der Wortlaut der SEA zur Farbe, wo sie eine nennt. Er steht getrennt vom
	 * Klartext, damit die Legende nicht von Belegen zugestellt wird, der Beleg
	 * aber trotzdem am Wert haengt und nicht verlorengeht.
	 */
	sea?: string;
}

/** Grundfarbe und Klartext je Horizontsymbol. */
export const HORIZONT_FARBEN: Readonly<Record<string, HorizontEintrag>> = {
	// --- organische Auflage (liegt UEBER der Gelaendeoberflaeche) -------------
	'Ol': { farbe: '#9c7746', text: 'Förna-Subhorizont (Streu)' },
	'Of': { farbe: '#6f4f2c', text: 'Vermoderungs-Subhorizont' },
	'Oh': { farbe: '#3c2b1b', text: 'Feinhumus-Subhorizont' },
	'O': { farbe: '#4d3722', text: 'Sammelsymbol organische Decken' },

	// --- Torf und Mudde ------------------------------------------------------
	'H': { farbe: '#33241a', text: 'Torfhorizont' },
	'Hv': { farbe: '#3a2a1a', text: 'Vererdungshorizont', sea: 'dunkelbraun bis braunschwarz' },
	'Hm': { farbe: '#221a14', text: 'Torfvermulmungshorizont', sea: 'schwarz' },
	'Hs': { farbe: '#3d2c1e', text: 'Torfschrumpfungshorizont' },
	'Ha': { farbe: '#42301f', text: 'Torfbröckelhorizont' },
	'F': { farbe: '#6a6a52', text: 'Muddehorizont' },

	// --- humose Mineralbodenhorizonte ----------------------------------------
	// Aa ist mit 15 bis 30 % organischer Substanz schon fast ein Torf und
	// bleibt dunkel. Ah, Ap und A dagegen NICHT: Wie dunkel ein humoser
	// Oberboden ist, sagt seine Humusstufe an der Substratzeile, nicht sein
	// Symbol. Eine dunkle Symbolfarbe zaehlte dieselbe Ursache zweimal und
	// machte die Stufen unleserlich (Nutzer-Befund 19.09.2026).
	'Aa': { farbe: '#3b332a', text: 'A-Horizont, über 15 bis 30 % organische Substanz' },
	'Ah': { farbe: '#7a7164', text: 'mit Humus angereichert, ungestörte Lagerung' },
	'Ap': { farbe: '#80776a', text: 'Pflughorizont' },
	'A': { farbe: '#7a7164', text: 'humoser Oberbodenhorizont' },

	// --- Auswaschung (Eluvial) -----------------------------------------------
	'E': { farbe: '#dcd8cd', text: 'Sammelsymbol Auswaschungshorizonte' },
	'Es': { farbe: '#dfdbd1', text: 'Sesquioxydauswaschungshorizont' },
	'Et': { farbe: '#dad6ca', text: 'Tonverarmungshorizont' },
	'Eh': { farbe: '#cec8ba', text: 'Humusauswaschungshorizont' },

	// --- Einwaschung und Verbraunung (Illuvial) ------------------------------
	'B': { farbe: '#a1774c', text: 'Sammelsymbol Einwaschungshorizonte' },
	'Bv': { farbe: '#ab7b4a', text: 'Braunhorizont (Verbraunung)' },
	'Bt': { farbe: '#8e5c37', text: 'Tonhäutchenhorizont' },
	// Eine Spur roetlicher als die uebrigen B-Horizonte (Nutzer-Wunsch
	// 19.09.2026): Es ist die Eiseneinwaschung, die ihn faerbt. Gilt damit auch
	// fuer „(Bs)", das seine Farbe von hier bekommt.
	'Bs': { farbe: '#a84826', text: 'Sesquioxydeinwaschungshorizont' },
	'Bh': { farbe: '#6b584a', text: 'Humuseinwaschungshorizont' },
	'Bsh': { farbe: '#7d4a2e', text: 'Humus- und Sesquioxydeinwaschungshorizont' },
	'Bi': { farbe: '#98643f', text: 'Einwaschungshorizont (Bs und Bh zusammengefasst)' },
	'Bb': { farbe: '#b18a5c', text: 'Bänderhorizont (Einschlämmungsbändchen im Sand)' },

	// --- Untergrund ----------------------------------------------------------
	'C': { farbe: '#d8c8a2', text: 'merkmalsarmer Untergrund' },
	'Ca': { farbe: '#e8e0c8', text: 'Karbonatanreicherungshorizont' },

	// --- verlagertes Material ------------------------------------------------
	'M': { farbe: '#8b7d69', text: 'Mineralboden mit synsedimentärer Humusanreicherung' },

	// --- Grundwasser ---------------------------------------------------------
	'G': { farbe: '#b3a98a', text: 'Gleyhorizont', sea: 'Rostfleckigkeit nach unten abnehmend' },
	'Go': { farbe: '#c2a05c', text: 'Rostabsatz-Subhorizont', sea: 'rostfleckig' },
	'Gr': { farbe: '#8fa79f', text: 'Reduktions-Subhorizont', sea: 'bleich grünblau' },

	// --- Stauwasser ----------------------------------------------------------
	'S': { farbe: '#a8a294', text: 'Sammelsymbol Staunässehorizonte' },
	'Sw': { farbe: '#b6b2a6', text: 'Stauwasserleiter', sea: 'vorherrschend graue Farben' },
	'Sd': { farbe: '#9b9890', text: 'wasserstauender Horizont', sea: 'grau und rostfarben marmoriert' },
};

export type Muster =
	'keins' | 'rost' | 'rost-wenig' | 'bleich' | 'marmor' | 'baender' | 'streu' | 'fasern'
	| 'broeckel' | 'wurzeln';

/** Muster, das ueber die Grundfarbe eines Horizontes gelegt wird. */
export const HORIZONT_MUSTER: Readonly<Record<string, Muster>> = {
	// Der humose Oberboden ist durchwurzelt - das gehoert zu seinem Bild.
	'Ah': 'wurzeln', 'Ap': 'wurzeln', 'Aa': 'wurzeln', 'A': 'wurzeln',
	'Go': 'rost',        // rostfleckig (SEA)
	'G': 'rost-wenig',   // Rostfleckigkeit nach unten abnehmend (SEA)
	'Gr': 'keins',
	'Sw': 'bleich',      // Bleich- und Rostflecke, vorherrschend grau
	'Sd': 'marmor',      // grau und rostfarben marmoriert, dichter
	'S': 'marmor',
	'Bb': 'baender',     // braune Baender
	'Ol': 'streu',
	'Of': 'streu',
	'H': 'fasern', 'Hv': 'fasern', 'Hm': 'keins', 'Hs': 'fasern', 'Ha': 'broeckel',
	'F': 'keins',
};

/**
 * Wie viel „Baenderhorizont" in einer Schicht steckt.
 *
 * Nutzer-Vorgabe 21.09.2026: Bb↔C und erst recht (Bb↔C) liegen nahe am C,
 * hell mit wenigen dunklen Baendern; C↔Bb und (C↔Bb) etwas dunkler und mit
 * etwas mehr Baendern, aber immer noch heller als Bb und (Bb). Bb und (Bb)
 * tragen die meisten Baender - hoechstens so viele wie bis dahin, denn das
 * waren schon zu viele. Bis dahin sah C↔Bb genau aus wie Bb (es nahm dessen
 * Farbe), und jeder Wechsel zeigte gleich viele Baender.
 *
 * Die Reihe von hell nach dunkel: C, (Bb↔C), Bb↔C, (C↔Bb), C↔Bb, (Bb), Bb.
 *
 * - `zuBb`: wie weit die Grundfarbe eines Wechsels von C nach Bb rueckt
 *   (0 = C, 1 = Bb). Nur fuer die Wechsel; Bb und (Bb) behalten ihre Farbe.
 *   Die Klammer steckt hier schon drin - ein schwacher Wechsel wird deshalb
 *   NICHT zusaetzlich zum grauen unveraenderten Boden hin gemischt, sonst
 *   rueckte er vom C weg statt darauf zu.
 * - `dichte`: wie viele Baender gezeichnet werden (1 = der Stand bis
 *   21.09.2026).
 *
 * Das ist eine Darstellungsentscheidung: Wie viele Baender wirklich im Boden
 * liegen, sagt das Symbol nicht.
 */
export interface BaenderStufe {
	zuBb: number;
	dichte: number;
}

export const BAENDER_STUFEN: Readonly<Record<string, BaenderStufe>> = {
	'(Bb↔C)': { zuBb: 0.06, dichte: 0.22 },
	'Bb↔C': { zuBb: 0.15, dichte: 0.32 },
	'(C↔Bb)': { zuBb: 0.35, dichte: 0.5 },
	'C↔Bb': { zuBb: 0.45, dichte: 0.58 },
	'(Bb)': { zuBb: 1, dichte: 0.7 },
	'Bb': { zuBb: 1, dichte: 0.8 },
};

/** Die Baenderstufe eines Symbols, oder `null`, wenn es keine Baender traegt. */
export function baenderStufe(roh: string | undefined | null): BaenderStufe | null {
	const g = leseSymbol(roh);
	return BAENDER_STUFEN[g.schwach ? `(${g.kern})` : g.kern] ?? null;
}

export interface GelesenesSymbol {
	/** Das Symbol, wie es erfasst wurde. */
	roh: string;
	/** Ohne Klammern und ohne nachgestelltes „r". */
	kern: string;
	/** Runde Klammern: schwache Auspraegung. */
	schwach: boolean;
	/** Nachgestelltes „r": reliktischer Grundgley-Horizont. */
	reliktisch: boolean;
}

/**
 * Zerlegt ein Horizontsymbol.
 *
 * Belegt in SEA 95 Teil A, Abschnitt 1.3.1 (S. 40 f.):
 *  - runde Klammern = schwache Auspraegung, z.B. (Bv)
 *  - nachgestelltes „r" = reliktischer Grundgley-Horizont, z.B. Go r
 *  - Mischhorizonte durch Nebeneinandersetzen, der praegende Teil steht an
 *    LETZTER Stelle (z.B. EtBv, AhG)
 *  - Baenderhorizont Bb mit Anteilen in Zehntelstufen (9:1, 8:2 ...)
 */
export function leseSymbol(roh: string | undefined | null): GelesenesSymbol {
	const s = (roh ?? '').trim();
	let kern = s;
	let schwach = false;
	let reliktisch = false;

	if (/^\(.*\)$/.test(kern)) {
		schwach = true;
		kern = kern.slice(1, -1).trim();
	}
	if (/\sr$/.test(kern)) {
		reliktisch = true;
		kern = kern.replace(/\sr$/, '').trim();
	}
	return { roh: s, kern, schwach, reliktisch };
}

/** Anteilsangabe wie „8:2" (Zehntelstufen beim Baenderhorizont). */
export function istAnteil(roh: string | undefined | null): boolean {
	return /^\d\s*:\s*\d$/.test((roh ?? '').trim());
}

/** Wechselhorizont wie „Bb↔C": beide Teile liegen ineinander. */
export function istWechsel(roh: string | undefined | null): boolean {
	return (roh ?? '').includes('↔');
}

/**
 * Die Symbole einer Horizontzeile als ein Text.
 *
 * Die SEA setzt Mischhorizonte OHNE Trennzeichen nebeneinander („EtBv", „AhG").
 * Das bleibt die Regel - „Ah/Es" sieht fuer einen Mischhorizont falsch aus.
 *
 * Traegt aber eines der Symbole selbst ein Leerzeichen, klebt alles zusammen
 * und wird unlesbar: aus „Go r" und „Gr r" wuerde „Go rGr r". Dann trennt ein
 * Mittelpunkt (Nutzer-Befund 19.09.2026). Er ist in der Bodenkunde kein
 * belegtes Zeichen und laesst sich deshalb nicht mit einem Symbolteil
 * verwechseln - anders als der Schraegstrich, der fuer UEBERGANGShorizonte
 * steht (SEA: „Et/Bt", „Go/Gr").
 *
 * Steht hier EINMAL, damit Bild, Tafel und Legende dieselbe Schreibweise
 * benutzen.
 */
export function symbolfolgeText(symbole: (string | undefined | null)[] | undefined | null): string {
	const liste = (symbole ?? []).filter((s): s is string => !!s).map(s => s.trim());
	if (liste.length < 2) return liste.join('');
	const mitLuecke = liste.some(s => /\s/.test(s));
	return liste.join(mitLuecke ? ' · ' : '');
}

/** Klartext zu einem Symbol, mit den Zusaetzen aus Klammer und „r". */
export function symbolText(roh: string): string {
	const g = leseSymbol(roh);
	if (istWechsel(g.kern)) return 'Wechsel zweier Horizonte, Anteile in Zehntelstufen';
	const eintrag = HORIZONT_FARBEN[g.kern];
	const zusatz: string[] = [];
	if (g.schwach) zusatz.push('schwach ausgeprägt');
	if (g.reliktisch) zusatz.push('reliktisch');
	const grund = eintrag ? eintrag.text : 'im Katalog des Grubenbildes nicht hinterlegt';
	return grund + (zusatz.length ? ` (${zusatz.join(', ')})` : '');
}

/* --- Farbwerkzeuge -------------------------------------------------------- */

export interface Hsl { h: number; s: number; l: number; }

export function hexZuHsl(hex: string): Hsl {
	const n = parseInt(hex.slice(1), 16);
	return rgbZuHsl({ r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 });
}

/** RGB (0 bis 255) nach HSL. */
export function rgbZuHsl(farbe: { r: number; g: number; b: number }): Hsl {
	const r = farbe.r / 255, g = farbe.g / 255, b = farbe.b / 255;
	const max = Math.max(r, g, b), min = Math.min(r, g, b);
	let h = 0, s = 0;
	const l = (max + min) / 2;
	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
		else if (max === g) h = (b - r) / d + 2;
		else h = (r - g) / d + 4;
		h /= 6;
	}
	return { h: h * 360, s: s * 100, l: l * 100 };
}

export function hsl(h: number, s: number, l: number): string {
	const k = (v: number) => Math.max(0, Math.min(100, v));
	return `hsl(${h.toFixed(0)} ${k(s).toFixed(0)}% ${k(l).toFixed(0)}%)`;
}

/**
 * Liest eine Farbe, gleich ob sie als Hex, als `hsl(...)` oder als `rgb(...)`
 * vorliegt.
 *
 * ⚠ `rgb(...)` MUSS mit dabei sein, aus demselben Grund wie bei `alsRgb`:
 * `mischen` und `mischenMit` liefern dieses Format, und ihre Ergebnisse werden
 * weiterverarbeitet (aufhellen, entsaettigen, Helligkeitsband). Fehlte der
 * Zweig, liefe jede gemischte Farbe in den Rueckfall und kaeme als mittleres
 * GRAU zurueck - der Farbton waere still weg. Genau das ist am 19.09.2026
 * passiert, als „schwach ausgepraegt" auf Mischung umgestellt wurde: „(Bv)"
 * war plotzlich grau statt braun.
 */
export function alsHsl(farbe: string): Hsl {
	if (farbe.charAt(0) === '#') return hexZuHsl(farbe);
	const m = farbe.match(/hsl\(([\d.]+) ([\d.]+)% ([\d.]+)%\)/);
	if (m) return { h: +m[1], s: +m[2], l: +m[3] };
	if (/^rgba?\(/.test(farbe)) return rgbZuHsl(alsRgb(farbe));
	return { h: 0, s: 0, l: 60 };
}

/** Heller (positiv) oder dunkler (negativ) und entsaettigt (0 bis 1). */
export function tonen(farbe: string, heller = 0, entsaettigen = 0): string {
	const c = alsHsl(farbe);
	return hsl(c.h, c.s * (1 - entsaettigen), c.l + heller);
}

/** Helligkeit 0 bis 100 - entscheidet, ob Schrift darauf hell oder dunkel ist. */
export function helligkeit(farbe: string): number {
	return alsHsl(farbe).l;
}

/**
 * Eine Farbe als RGB - Hex, `hsl(...)` oder `rgb(...)`.
 *
 * `rgb(...)` muss mit dabei sein: `mischen` gibt dieses Format zurueck, und
 * seine Ergebnisse werden fuer die Zwischenstufen des Verlaufs ein zweites Mal
 * gemischt. Ohne diesen Zweig faende der Leser nichts und liefe in den
 * Rueckfall - eine still falsche Farbe.
 */
export function alsRgb(farbe: string): { r: number; g: number; b: number } {
	if (farbe.charAt(0) === '#') {
		const n = parseInt(farbe.slice(1), 16);
		return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
	}
	const m = farbe.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/);
	if (m) return { r: +m[1], g: +m[2], b: +m[3] };
	return hslZuRgb(alsHsl(farbe));
}

export function hslZuRgb({ h, s, l }: Hsl): { r: number; g: number; b: number } {
	const ss = s / 100, ll = l / 100;
	const c = (1 - Math.abs(2 * ll - 1)) * ss;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = ll - c / 2;
	let r = 0, g = 0, b = 0;
	if (h < 60) { r = c; g = x; }
	else if (h < 120) { r = x; g = c; }
	else if (h < 180) { g = c; b = x; }
	else if (h < 240) { g = x; b = c; }
	else if (h < 300) { r = x; b = c; }
	else { r = c; b = x; }
	return {
		r: Math.round((r + m) * 255),
		g: Math.round((g + m) * 255),
		b: Math.round((b + m) * 255),
	};
}

/**
 * Mittelfarbe zweier Farben, fuer den weichen Uebergang an einer Grenze.
 *
 * ⚠ Gemischt wird im RGB-Raum, NICHT ueber den Farbton.
 *
 * Eine Mischung ueber den Farbkreis nimmt den kuerzesten Weg von einem Ton zum
 * anderen - und der fuehrt zwischen rostbraun (rund 35 Grad) und bleich
 * gruenblau (rund 160 Grad) mitten durch GRUEN. Am Uebergang stand dann ein
 * gruenlicher Streifen, den keine der beiden Schichten hat
 * (Nutzer-Befund 19.09.2026). Im RGB-Raum entsteht statt dessen ein
 * gedaempftes Graubraun: eine Farbe, die zwischen beiden liegt.
 */
export function mischen(a: string, b: string): string {
	const x = alsRgb(a), y = alsRgb(b);
	const m = (p: number, q: number) => Math.round((p + q) / 2);
	return `rgb(${m(x.r, y.r)} ${m(x.g, y.g)} ${m(x.b, y.b)})`;
}

/**
 * Mischung mit Gewicht: `anteilB` von 0 (nur a) bis 1 (nur b).
 *
 * Auch hier im RGB-Raum, aus demselben Grund wie bei `mischen`.
 */
export function mischenMit(a: string, b: string, anteilB: number): string {
	const x = alsRgb(a), y = alsRgb(b);
	const t = Math.max(0, Math.min(1, anteilB));
	const m = (p: number, q: number) => Math.round(p * (1 - t) + q * t);
	return `rgb(${m(x.r, y.r)} ${m(x.g, y.g)} ${m(x.b, y.b)})`;
}

/** Die Farbe eines unveraenderten Bodens, ohne jedes Merkmal. */
export const NEUTRALER_BODEN = '#cfc9bd';

/**
 * Ein Boden ist weder schwarz noch weiss.
 *
 * Gemessen am 19.09.2026: Ein Hv mit h7 landete auf Helligkeit 0, also reinem
 * Schwarz - darin ist keine Faser mehr zu sehen -, ein „(Es)" auf 94, also
 * praktisch Weiss. Nebeneinander ergab das einen Sprung von 58
 * Helligkeitspunkten, den der Nutzer zu Recht als zu hart gemeldet hat. Die
 * Schranken kappen nur die Ausreisser; alles dazwischen bleibt, wie es war.
 */
export const HELLIGKEIT_UNTEN = 16;
export const HELLIGKEIT_OBEN = 88;

export function inHelligkeitsband(farbe: string): string {
	const c = alsHsl(farbe);
	const l = Math.max(HELLIGKEIT_UNTEN, Math.min(HELLIGKEIT_OBEN, c.l));
	return l === c.l ? farbe : hsl(c.h, c.s, l);
}

/* --- Die Farbe einer Schicht ---------------------------------------------- */

export interface SchichtBild {
	farbe: string;
	/** Das praegende Symbol ohne Klammern und ohne nachgestelltes „r". */
	kern: string;
	muster: Muster;
	schwach: boolean;
	/**
	 * Wie kraeftig das Muster gezeichnet wird (0 bis 1).
	 *
	 * Sie faellt bei schwacher Auspraegung und bei einem reliktischen Horizont,
	 * und beides zaehlt zusammen: siehe `KLAMMER_MUSTER`, `KLAMMER_BAENDER` und
	 * `RELIKT_MUSTER`.
	 */
	musterStaerke: number;
	/**
	 * Wie viele Baender gezeichnet werden (1 = der Stand bis 21.09.2026).
	 * Nur fuer das Muster „baender" von Bedeutung, siehe `BAENDER_STUFEN`.
	 */
	baenderDichte: number;
}

/**
 * Wie kraeftig das Muster einer SCHWACHEN Auspraegung noch gezeichnet wird.
 *
 * Nutzer-Vorgabe 22.09.2026: Ein Horizont in Klammern soll sich deutlich von
 * seinem Pendant ohne Klammer unterscheiden. Bis dahin stand hier 0,7, und das
 * war zu wenig: Der Wert wirkt auf die ZAHL der Flecken UND auf ihre Deckkraft
 * (siehe `muster` in `grubenbild-zeichnen.ts`), beide Male gleich - aus 0,7
 * wird im Bild also rund die halbe Fleckenmenge, und ein halb so stark
 * geflecktes Go sieht immer noch nach Go aus.
 *
 * Bei 0,35 bleibt rund ein Achtel der Fleckenwirkung: sichtbar, dass hier ein
 * Merkmal liegt, aber auf den ersten Blick vom vollen Horizont zu
 * unterscheiden.
 */
export const KLAMMER_MUSTER = 0.35;

/**
 * Dasselbe fuer einen eingeklammerten Baenderhorizont - und zwar schwaecher
 * gedaempft.
 *
 * Bei den Baendern steckt die Klammer schon in der Baenderstufe: „(Bb)" traegt
 * laut `BAENDER_STUFEN` weniger Baender als „Bb" (Nutzer-Vorgabe 21.09.2026).
 * Wer hier zusaetzlich die allgemeine Daempfung auflegt, rechnet die Klammer
 * zweimal und verschiebt die abgestimmte Reihe. Der Wert ist deshalb der
 * bisherige.
 */
export const KLAMMER_BAENDER = 0.7;

/**
 * Wie kraeftig das Muster eines RELIKTISCHEN Horizontes noch gezeichnet wird.
 *
 * Ein reliktischer Horizont ist an verblassten Merkmalen zu erkennen: Wenn
 * seine Farbe gedaempft wird, muss auch seine Fleckung zuruecktreten - sonst
 * saehe ein „Go r" so frisch rostig aus wie ein „Go".
 */
export const RELIKT_MUSTER = 0.5;

/**
 * Farbe und Muster aus den Horizontsymbolen einer Zeile.
 *
 * Praegend ist das LETZTE Symbol (SEA: „Der praegende Horizontteil steht an
 * letzter Stelle"); die davorstehenden toenen es. Schwache Auspraegung hellt
 * auf und entsaettigt, „reliktisch" daempft.
 */
export function horizontFarbe(symbole: (string | undefined | null)[] | undefined | null): SchichtBild {
	const gelesen = (symbole ?? [])
		.filter((s): s is string => !!s)
		.filter(s => !istAnteil(s))
		.map(leseSymbol);
	if (!gelesen.length) {
		return {
			farbe: NEUTRALER_BODEN, kern: '', muster: 'keins', schwach: false, musterStaerke: 1,
			baenderDichte: 1,
		};
	}

	const letzter = gelesen[gelesen.length - 1];
	let kern = letzter.kern;
	let musterExtra: Muster | null = null;
	// Wechselhorizont „Bb↔C": das zweite Glied ist die Grundmasse, das erste
	// liegt als Baender darin.
	if (istWechsel(kern)) {
		const teile = kern.split('↔').map(t => t.trim());
		kern = teile[1] || teile[0];
		musterExtra = teile.includes('Bb') ? 'baender' : null;
	}

	// Ein Wechsel aus Bb und C liegt zwischen beiden, wie weit, sagt seine
	// Baenderstufe (Nutzer-Vorgabe 21.09.2026). Vorher nahm er die Farbe
	// seines letzten Gliedes, und C↔Bb sah genau aus wie Bb.
	const wechselStufe = istWechsel(letzter.kern) ? baenderStufe(letzter.roh) : null;
	const eintrag = HORIZONT_FARBEN[kern];
	let farbe = wechselStufe
		? mischenMit(HORIZONT_FARBEN['C'].farbe, HORIZONT_FARBEN['Bb'].farbe, wechselStufe.zuBb)
		: (eintrag ? eintrag.farbe : NEUTRALER_BODEN);
	let heller = 0, entsaettigen = 0;

	// „Schwach ausgepraegt" und „reliktisch" heissen: Das Merkmal ist weniger
	// zu sehen. Die Farbe rueckt deshalb in Richtung des UNVERAENDERTEN Bodens
	// und wird nicht einfach heller. Bei einem dunklen Bv wirkt das
	// aufhellend, bei einem ohnehin fast weissen Es dagegen leicht
	// abdunkelnd - „heller" allein trieb „(Es)" auf Helligkeit 94, also
	// praktisch Weiss (Messung 19.09.2026).
	//
	// Beim Wechsel steckt die Klammer schon in der Baenderstufe: „schwach"
	// heisst dort „naeher am C", nicht „grauer".
	if (letzter.schwach && !wechselStufe) {
		farbe = mischenMit(farbe, NEUTRALER_BODEN, 0.3);
		entsaettigen += 0.2;
	}
	if (letzter.reliktisch) {
		farbe = mischenMit(farbe, NEUTRALER_BODEN, 0.22);
		entsaettigen += 0.4;
		// Gr r: die Bleichung ist so weit gegangen, dass der Horizont weiss
		// erscheint (Angabe des Kartierers, 19.09.2026).
		if (kern === 'Gr') { heller += 16; entsaettigen += 0.3; }
	}

	// Ein vorangestelltes Symbol toent mit: AhEs = Bleichhorizont mit Humus.
	if (gelesen.length > 1) {
		const vorn = HORIZONT_FARBEN[gelesen[0].kern];
		if (vorn) {
			const a = alsHsl(farbe), b = alsHsl(vorn.farbe);
			farbe = hsl(a.h, a.s * 0.72 + b.s * 0.28, a.l * 0.68 + b.l * 0.32);
		}
	}

	// Klammer und „r" zaehlen BEIDE, nicht nur das staerkere von beiden:
	// „(Go r)" ist ein schwach ausgepraegter reliktischer Rostabsatz und damit
	// das blasseste der drei. Bis zum 22.09.2026 gewann das „r" allein, und
	// „(Go r)" sah genau aus wie „Go r".
	let musterStaerke = 1;
	if (letzter.schwach) {
		musterStaerke *= baenderStufe(letzter.roh) ? KLAMMER_BAENDER : KLAMMER_MUSTER;
	}
	if (letzter.reliktisch) musterStaerke *= RELIKT_MUSTER;

	return {
		farbe: inHelligkeitsband(tonen(farbe, heller, entsaettigen)),
		kern,
		muster: musterExtra ?? musterAus(kern, gelesen),
		schwach: letzter.schwach,
		musterStaerke,
		baenderDichte: baenderDichteAus(gelesen),
	};
}

/**
 * Wie viele Baender die Schicht bekommt.
 *
 * Es zaehlt das Symbol, das die Baender bringt - dieselbe Reihenfolge wie bei
 * `musterAus`: zuerst das praegende, sonst das erste der uebrigen. Ohne
 * Baendersymbol bleibt es bei 1 (die Zahl wird dann nicht gebraucht).
 */
function baenderDichteAus(gelesen: GelesenesSymbol[]): number {
	const eigene = baenderStufe(gelesen[gelesen.length - 1].roh);
	if (eigene) return eigene.dichte;
	for (const g of gelesen) {
		const stufe = baenderStufe(g.roh);
		if (stufe) return stufe.dichte;
	}
	return 1;
}

/**
 * Welches Muster die Schicht bekommt.
 *
 * Zuerst das praegende Symbol. Hat es keines, entscheidet das erste der
 * uebrigen Symbole, das eines mitbringt: Ein Baenderhorizont (Bb) ist ein
 * Formmerkmal und keine Farbe. Steht er in der Zeile, muss man die Baender
 * sehen, auch wenn ein anderes Symbol die Farbe vorgibt (Fall „Bb / Go r /
 * Gr r", Grube 033 Torgelow). Gezeichnet wird immer nur EIN Muster; zwei
 * uebereinander ergeben Matsch.
 */
export function musterAus(kern: string, gelesen: GelesenesSymbol[]): Muster {
	const eigenes = HORIZONT_MUSTER[kern];
	if (eigenes && eigenes !== 'keins') return eigenes;
	for (const g of gelesen) {
		let k = g.kern;
		if (istWechsel(k)) k = k.split('↔')[0].trim();
		const m = HORIZONT_MUSTER[k];
		if (m && m !== 'keins') return m;
	}
	return 'keins';
}

/**
 * Wie stark der Humusgehalt die Horizontfarbe abdunkelt (Abzug an Helligkeit
 * als ZIELHELLIGKEIT der Schicht.
 *
 * Der Humusgehalt ueberpraegt alles andere (Nutzer-Vorgabe 19.09.2026): Wie
 * dunkel eine Schicht ist, sagt ihre Humusstufe; der Horizont steuert nur noch
 * den Farbton und einen kleinen Rest Helligkeit bei. Die Reihe folgt den
 * Spannen der Stufen, die nach oben stark zunehmen (h2 = 1 bis 2,5 %,
 * h6 = 15 bis 30 %).
 *
 * ⚠ Zwei fruehere Fassungen sind daran gescheitert, dass sie VON der
 * Horizontfarbe aus rechneten - erst mit festen Prozentpunkten, dann mit einem
 * Anteil, zuletzt als blosse Obergrenze. Dabei blieb das Ergebnis am Horizont
 * haengen: Am Bild gemessen war ein „Ah(Es) h3“ HELLER als ein
 * „Ap(Bi) h2“ (Nutzer-Befund), weil der helle Bleichhorizont den hoeheren
 * Humusgehalt schlug; als Obergrenze blieb davon ein Unterschied von einem
 * einzigen Helligkeitspunkt - sichtbar ist das nicht.
 *
 * `HUMUS_EIGENANTEIL` ist der Rest, den der Horizont noch beitragen darf. Er
 * ist mit Absicht kleiner als der halbe Abstand zweier Stufen (die engste ist
 * h6 nach h7 mit 4 Punkten): Nur so kann eine hoehere Stufe NIE heller
 * herauskommen als eine niedrigere.
 *
 * Die Werte liegen bewusst TIEFER als die Horizontfarben der gewoehnlichen
 * Mineralhorizonte. Lagen sie darueber, biss die Stufe bei einem etwas
 * dunkleren Horizont gar nicht - genau daran scheiterte die Fassung davor:
 * „Ah(Es) h3“ und „Ap(Bi) h2“ trennte am Ende ein einziger
 * Helligkeitspunkt.
 */
export const HUMUS_HELLIGKEIT: readonly number[] = [100, 60, 50, 42, 35, 29, 24, 20];

/** Wie viel Helligkeit der Horizont neben der Humusstufe noch beitragen darf. */
export const HUMUS_EIGENANTEIL = 2;

/**
 * Wie viele Helligkeitspunkte die Humusstufe an dieser Farbe aendert.
 *
 * Die Stufe nennt eine Zielhelligkeit; der Horizont darf davon noch
 * `HUMUS_EIGENANTEIL` Punkte abweichen. Der Humus DUNKELT dabei nur, er hellt
 * nie auf: Eine Schicht mit „sehr schwach humos“ darf nicht heller werden als
 * dieselbe Schicht ohne Humusangabe, und ein Torfhorizont wird nicht hell,
 * weil jemand dort eine niedrige Stufe eingetragen hat (Nutzer-Befund
 * 19.09.2026: Im Bild stand ein h1 heller als das h0 darunter).
 */
function humusAenderung(farbe: string, stufe: number): number {
	const jetzt = alsHsl(farbe).l;
	const eigen = Math.max(-HUMUS_EIGENANTEIL,
		Math.min(HUMUS_EIGENANTEIL, (jetzt - 55) * 0.1));
	return Math.min(0, HUMUS_HELLIGKEIT[stufe] + eigen - jetzt);
}

/**
 * Die endgueltige Farbe einer Schicht: Horizontfarbe, vom Humusgehalt der
 * Substratzeile an derselben Tiefe ueberpraegt.
 */
export function schichtFarbe(
	symbole: (string | undefined | null)[] | undefined | null,
	humusgehalt?: number,
): SchichtBild {
	const hf = horizontFarbe(symbole);
	const h = typeof humusgehalt === 'number' && Number.isFinite(humusgehalt)
		? Math.max(0, Math.min(7, Math.round(humusgehalt)))
		: 0;
	if (!h) return hf;
	return {
		...hf,
		farbe: inHelligkeitsband(
			tonen(hf.farbe, humusAenderung(hf.farbe, h), Math.min(0.55, h * 0.06)),
		),
	};
}
